import { useTranslation } from 'react-i18next'
import { observe, useObserver, viewportWidth } from 'react-ui-observer'
import get from 'lodash/get'
import styled, { css } from 'styled-components'

import { breakpoints } from 'bl-common/src/constants/breakpoints'
import { colors } from 'bl-common/src/constants/colors'
import { durations } from 'bl-common/src/constants/durations'
import { Link } from 'bl-common/src/elements/Link'
import { Type } from 'bl-common/src/elements/Typography/Typography'
import { ResponsiveParallax } from 'bl-common/src/units/ResponsiveParallax'
import { Section } from 'bl-common/src/units/Section/Section'
import { getImageUrl } from 'bl-common/src/units/SmartImage/getImageUrl'
import { StyledImage } from 'bl-common/src/units/SmartImage/StyledImage'
import { media } from 'bl-common/src/utils/media'
import { formatDateInUTC } from 'bl-utils/src/formatting/formatDate'

import { StoryCard } from './StoryCard'

const StoryContainer = styled.div`
  display: flex;
`

const ParallaxWrap = styled.div`
  position: relative;
  flex: 1;

  ${media.md(css`
    flex: 0 1 60%;
  `)}

  ${media.lg(css`
    flex: 0 1 550px;
  `)}
`

const Content = styled.div`
  flex: 1;

  ${media.md(css`
    padding: ${({ theme }) => theme.spacing[2]};
  `)}

  ${media.lg(css`
    padding: ${({ theme }) => theme.spacing[4]};
  `)}
`

const ImageWrap = styled.div`
  position: relative;
  top: -32px;
  overflow: hidden;
  width: 100%;
`

const Image = styled(StyledImage)<{ ratio: number }>`
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  z-index: 1;

  width: calc(100% - 48px);
  ${media.md(css`
    width: calc(100% - 86px);
  `)}

  &::before {
    content: '';
    display: block;
    padding-bottom: ${({ ratio }) => ratio * 100}%;
  }

  transform: scale(1);
  opacity: 0.9;
  transition:
    opacity ${durations.short}ms,
    transform ${durations.long}ms;
`

// OuterLink is the main hover trigger
const OuterLink = styled(({ ...rest }) => <Link {...rest} />)`
  position: absolute;
  bottom: 0;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;

  ${media.md(css`
    &:hover {
      & + ${ImageWrap} > ${Image} {
        transform: scale(1.035);
        opacity: 1;
      }
    }
  `)}
`

const InnerLink = styled(({ ...rest }) => <Link {...rest} />)`
  position: relative;
  z-index: 2;
  display: block;

  &:hover {
    color: ${colors.softBlue};
  }
`

const DateWrap = styled.div`
  display: flex;
  opacity: 0.7;
  &::before {
    content: '—';
    display: block;
    margin-right: 10px;
  }
`

const GradientDecorator = styled(ResponsiveParallax)`
  position: absolute;
  background: linear-gradient(300deg, #d8e7e6 2.43%, #f5f6f5 97.27%);
  z-index: -1;
  top: 40px;
  bottom: 40px;
  left: 40%;
  right: 16px;

  ${media.md(css`
    top: 0;
    bottom: 40px;
    left: 100px;
    right: 0;
  `)}
`

const HeadlineWrap = styled.div`
  margin-bottom: 32px;
  ${media.md(css`
    margin-bottom: ${({ theme }) => theme.spacing[4]};
  `)}
`

const RenderStory = ({ image, link, topicSlug, topicName, title, date }) => {
  return (
    <StoryContainer>
      <OuterLink to={link} />
      <ParallaxWrap>
        <ImageWrap>
          <Image image={image} ratio={1.3} />
        </ImageWrap>
        <GradientDecorator from={8} to={2} disabledMobile />
      </ParallaxWrap>
      <Content>
        <Type
          preset="labelSmall"
          weight="bold"
          color={colors.deepBlue}
          case="uppercase"
          top={{ xs: 2 }}
          bottom={{ xs: 1 }}
        >
          <InnerLink to={`/stories/topic/${topicSlug}`}>{topicName}</InnerLink>
        </Type>
        <Type
          preset="headlineLarge"
          weight="bold"
          bottom={{ xs: 1.5 }}
          maxWidth={450}
        >
          {title}
        </Type>
        <DateWrap>
          <Type preset="text" bottom={{ xs: 4, md: 3 }}>
            {date}
          </Type>
        </DateWrap>
      </Content>
    </StoryContainer>
  )
}

export const LatestStoryStandalone = ({ story, top = {}, bottom = {} }) => {
  if (!story) {
    return null
  }
  const { t, i18n } = useTranslation()

  const { title, publishDate, topic, heroImage } = story.fields
  const date = formatDateInUTC(publishDate, 'PP', i18n.language)
  const topicSlug = get(topic, 'fields.topicSlug', '')
  const topicName = get(topic, 'fields.topicName', '')
  const image = get(heroImage, 'fields.image', '')
  const imageUrl = getImageUrl(image, { width: 2000 })

  // TODO: Switch to useBreakpoints
  const isMobile = useObserver(
    observe(viewportWidth(), width => width < breakpoints.md)
  )

  return (
    <Section top={top} bottom={bottom}>
      <HeadlineWrap>
        <Type preset="headlineMedium">Latest story</Type>
      </HeadlineWrap>

      {isMobile ? (
        <StoryCard
          imageRatio={1.3}
          title={title}
          topic={topic}
          date={publishDate}
          image={imageUrl}
          link={story.fields.slug}
        />
      ) : (
        <RenderStory
          image={imageUrl}
          title={title}
          topicName={topicName}
          topicSlug={topicSlug}
          date={date}
          link={t('storyPathWithSlug', { slug: story.fields.slug })}
        />
      )}
    </Section>
  )
}
